<template>
  <div class="max-w-4xl mx-auto px-4">
    <!-- 標題區塊 -->
    <div class="mb-8 text-center">
      <h1 class="text-4xl font-bold text-wood-800 mb-4 flex items-center justify-center">
        <i class="fas fa-book mr-3 text-wood-600"></i>
        拳譜管理
      </h1>
      <p class="text-wood-600 text-lg">
        管理太極拳拳譜資料與教學內容
      </p>
    </div>

    <!-- 載入狀態 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="text-center">
        <div class="w-16 h-16 border-4 border-wood-300 border-t-wood-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-wood-600 text-lg">載入拳譜資料中...</p>
      </div>
    </div>

    <!-- 拳譜列表 -->
    <div v-else-if="boxingCharts.length > 0" class="space-y-6">
      <div
        v-for="chart in boxingCharts"
        :key="chart.id"
        class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 post-shadow border-2 border-wood-200 hover:shadow-xl transition-all duration-300 hover:border-wood-400 hover:transform hover:scale-[1.01]"
      >
        <div class="flex items-start justify-between">
          <!-- 左側內容 -->
          <div class="flex-1">
            <div class="flex items-center mb-4">
              <div class="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <i class="fas fa-yin-yang text-white text-2xl animate-pulse-soft"></i>
              </div>
              <div>
                <h2 class="text-2xl font-bold text-wood-800 mb-1">{{ chart.name }}</h2>
                <div class="flex items-center space-x-2">
                  <span v-if="chart.json_file_exists" class="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                    <i class="fas fa-check-circle mr-1"></i>資料完整
                  </span>
                  <span v-else class="text-xs bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full font-medium">
                    <i class="fas fa-exclamation-triangle mr-1"></i>資料待補
                  </span>
                </div>
              </div>
            </div>

            <p class="text-wood-700 leading-relaxed mb-4">
              {{ chart.description }}
            </p>

            <!-- 操作按鈕 -->
            <div class="flex items-center space-x-3">
              <a
                :href="`/boxing_charts/${chart.id}`"
                class="text-wood-700 border-1 border-wood-300 px-6 py-2 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-md flex items-center"
              >
                <i class="fas fa-eye mr-2"></i>
                查看拳譜
              </a>

              <a
                v-if="isLoggedIn"
                :href="`/boxing_charts/${chart.id}/edit`"
                class="px-6 py-2 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-md flex items-center"
              >
                <i class="fas fa-edit mr-2"></i>
                編輯
              </a>

              <button
                v-if="isLoggedIn"
                @click="confirmDelete(chart)"
                class="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-md flex items-center"
              >
                <i class="fas fa-trash mr-2"></i>
                刪除
              </button>
            </div>
          </div>

          <!-- 右側箭頭 -->
          <div class="ml-6 flex items-center">
            <i class="fas fa-chevron-right text-wood-400 text-3xl"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 空狀態 -->
    <div v-else class="text-center py-20">
      <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-12 post-shadow">
        <i class="fas fa-book-open text-wood-400 text-6xl mb-6"></i>
        <h2 class="text-2xl font-bold text-wood-800 mb-4">目前沒有任何拳譜資料</h2>
        <p class="text-wood-600 mb-6">開始建立第一個拳譜,分享太極拳的精髓</p>
        <a
          v-if="isLoggedIn"
          href="/boxing_charts/new"
          class="inline-flex items-center px-8 py-3 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-gray rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
        >
          <i class="fas fa-plus mr-2"></i>
          新增第一個拳譜
        </a>
      </div>
    </div>

    <!-- 新增按鈕 (浮動) -->
    <div v-if="isLoggedIn && boxingCharts.length > 0" class="fixed bottom-8 right-8">
      <a
        href="/boxing_charts/new"
        class="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-gray rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110"
      >
        <i class="fas fa-plus text-2xl"></i>
      </a>
    </div>

    <!-- 刪除確認對話框 -->
    <teleport to="body">
      <div
        v-if="showDeleteDialog"
        @click="showDeleteDialog = false"
        class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <div
          @click.stop
          class="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8"
        >
          <div class="text-center mb-6">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i class="fas fa-exclamation-triangle text-red-600 text-3xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-gray-800 mb-2">確認刪除</h3>
            <p class="text-gray-600">
              確定要刪除「{{ chartToDelete?.name }}」嗎？<br>
              此操作無法復原。
            </p>
          </div>

          <div class="flex space-x-4">
            <button
              @click="showDeleteDialog = false"
              class="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl transition-colors font-medium"
            >
              取消
            </button>
            <button
              @click="deleteChart"
              class="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors font-medium"
            >
              確定刪除
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()
const boxingCharts = ref([])
const loading = ref(false)
const showDeleteDialog = ref(false)
const chartToDelete = ref(null)

const isLoggedIn = computed(() => userStore.isLoggedIn)

// 載入拳譜列表
const loadBoxingCharts = async () => {
  loading.value = true
  try {
    const response = await axios.get('/api/v1/boxing_charts')
    boxingCharts.value = response.data
    console.log('拳譜列表載入成功:', boxingCharts.value)
  } catch (error) {
    console.error('載入拳譜列表失敗:', error)
  } finally {
    loading.value = false
  }
}

// 確認刪除
const confirmDelete = (chart) => {
  chartToDelete.value = chart
  showDeleteDialog.value = true
}

// 刪除拳譜
const deleteChart = async () => {
  if (!chartToDelete.value) return

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    await axios.delete(`/boxing_charts/${chartToDelete.value.id}`, {
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })

    // 從列表中移除
    boxingCharts.value = boxingCharts.value.filter(c => c.id !== chartToDelete.value.id)

    showDeleteDialog.value = false
    chartToDelete.value = null

    console.log('拳譜刪除成功')
  } catch (error) {
    console.error('刪除拳譜失敗:', error)
    alert('刪除失敗，請稍後重試')
  }
}

onMounted(() => {
  console.log('拳譜列表頁面已載入')
  loadBoxingCharts()
})
</script>

<style scoped>
/* 太極圖動畫 */
@keyframes pulse-soft {
  0%, 100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(1.1) rotate(180deg);
    opacity: 0.8;
  }
}

.animate-pulse-soft {
  animation: pulse-soft 4s ease-in-out infinite;
}
</style>
